import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Icon from "@mui/material/Icon";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { cloneDeep, get, range, debounce } from "lodash";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import LoadingOverlay from "react-loading-overlay";
import { getGridData, storeGridData } from "actions/comparable";
import AppInsightsTracker from "components/Shared/AppInsightsTracker";
import * as sections from "components/Homeowner/CaseForms/ComparableGrid/Sections/Sections";
import { isValidNumFieldValue, postCompData, getAboveGradeUnitSubTableKeysFromGroupValue, aboveGradeUnitRoomCounts, belowGradeRoomCountOptions, reorder, adjustments, copySubjectBlacklist, isValidValue, hasRepeatedAddresses, hideExtraCarStorage, getGarageSectionsByFormCode, comparableCountByFormCode } from "components/Homeowner/CaseForms/ComparableGrid/Misc";
import { calculateAdjustmentAboveGrade, calculateAdjustmentGLA, calculateAdjTotal, calculateAdjustmentSite, calculateAdjustmentAge, updateAdjustment } from "components/Homeowner/CaseForms/ComparableGrid/Adjustment";
import { calculateUAD } from "components/Homeowner/CaseForms/ComparableGrid/UAD";
import { updateProximity } from "components/Homeowner/CaseForms/ComparableGrid/Proximity";
import { validateTable, validateField } from "components/Homeowner/CaseForms/ComparableGrid/Validate";
import { formSpecificSubjectDefinitions, formSpecificComparableSections } from "components/Homeowner/CaseForms/ComparableGrid/Sections/SectionDefinitions";
import SearchGooglePlaces from "components/FormElements/SearchGooglePlaces";
import { getStates } from "actions/region";
import { MSpinner } from "components/Shared";
import { dateTimeToString } from "components/Shared/Formatters";
import responseHandler from "responseHandler";
import { CompGridFormCodes } from "components/Homeowner/CaseForms/ComparableGrid/CompGridFormCodes";
import { validateComparables } from "requests/comparables";
import { savePNXFormSuccess, savePNXFormFail, saveCompPhotoMetadata } from "actions/homeowner/caseforms";
import WebConfig from "middlewares/WebConfig";
import { getCustomersByType } from "requests/customer";
import { BOTH } from "typeConstants/customerTypes";

class ComparableGrid extends AppInsightsTracker {
  constructor(props) {
    super(props);
    this.customer = React.createRef();
    this.updatingPhotosCompGrid = React.createRef();
  }
  state = {
    updated: false, // Only updated with getGridData
    isFetchingGrid: true,
    currentLoadedOnDate: new Date(),
    table: [],
    dropdownOpen: {
      concession: false,
      grade: false,
      dateOfSaleTime: false
    },
    site: { "Adj Threshold": "sf", "Adj Factor": "sf" },
    adjustment: {
      "above-grade": {},
      "basement-finished": {},
      "concession": {},
      "dateOfSaleTime": {},
      "site": {},
      "view": {},
      "age": {},
      "condition": {},
      "gla": {},
      "heating-cooling": {},
      "quality": {},
    },
    hideCarStorageExtra: true,
    disableAddCarStorageExtra: false,
    validationMessages: [],
    errorKeys: [],
    isPostingData: false,
    intervalId: null,
  };

  inputRefs = [];

  hasMouseOver = [];

  mounted = { current: true };
  
  // Make API call to get comparable and subject data
  componentDidMount() {
    const { getGridData, getStates, states, controlNumber } = this.props;
    if (Array.isArray(states) && states.length === 0) {
      getStates();
    }
    getGridData(controlNumber);
    getCustomersByType(BOTH, response => {
      this.customer.current = response;
    }, this.mounted);
  }

  // Updates subject and comparables data from API
  componentDidUpdate(prevProps, prevState) {
    const { gridData, states, pnxValidation, savePNXFormFail, savePNXFormSuccess, readOnly, controlNumber, token, compPhotoMetadata } = this.props;
    const { compGrid, settings, loadedOnDate } = this.props.gridData || {};
    const { subject } = compGrid || {};
    let { updated, currentLoadedOnDate } = this.state;

    if (updated && compGrid && loadedOnDate && (loadedOnDate !== currentLoadedOnDate)) {
      this.setState({ updated: false });
    }
    if (!updated && compGrid && compGrid.comparables && compGrid.comparables.length > 0 && Array.isArray(states) && states.length > 0) {
      let table = [formSpecificSubjectDefinitions(subject, settings, states, readOnly)];
      // If garage should be in expanded state
      let hideCarStorageExtra = this.state.hideCarStorageExtra;
      hideCarStorageExtra = hideCarStorageExtra && hideExtraCarStorage(table[0], "right", settings.compGridFormCode);
      // This updates data in comparables.
      compGrid.comparables.forEach((item) => {
        const comparable = formSpecificComparableSections(item, gridData, false, states, readOnly);
        // If garage should be in expanded state
        hideCarStorageExtra = hideCarStorageExtra && hideExtraCarStorage(comparable, "left", settings.compGridFormCode);
        table.push(comparable);
      });
      table = calculateUAD(table, settings);
      table = updateProximity(table);
      calculateAdjTotal(table, settings.compGridFormCode);
      this.setState({ table, updated: true, hideCarStorageExtra, isFetchingGrid: false, currentLoadedOnDate: loadedOnDate });

      //create refs
      for (let i = 0; i < table.length; i++) {
        this.inputRefs[i] = React.createRef();
      }
      this.getPhotosCompGrid();

    }

    // display error messages
    if (!pnxValidation.fetchingResult && prevProps.pnxValidation.fetchingResult) {
      // has errors?
      if (pnxValidation.validationMessages && pnxValidation.validationMessages.length) {
        if (!readOnly) savePNXFormFail("Save Successful with Failed Validation");
        this.setState({ validationMessages: pnxValidation.validationMessages.map(x => ({ fullErrorMessage: x })) });
      } else {
        if (!readOnly) savePNXFormSuccess();
        this.setState({ validationMessages: [] });
      }
    }

    // attach event listener
    if (prevState.table.length !== this.state.table.length) {
      for (let i = 0; i < this.state.table.length; i++) {
        this.inputRefs[i].current.addEventListener("scroll", () => this.handleScroll(i));
        this.inputRefs[i].current.addEventListener("mouseover", () => this.hasMouseOver[i] = true);
        this.inputRefs[i].current.addEventListener("mouseout", () => this.hasMouseOver[i] = false);
      }

      this.addScrollBarView(this.state.table);

      this.adjustSubjectHeight(this.state.table);
    }
    
    // calculate scroll bar height on every render
    this.timer = setTimeout(() => this.adjustScrollBarHeight(this.state.table), 300);

    if (controlNumber && token && this.customer.current && !compPhotoMetadata.length) this.getPhotosCompGrid();
    if (this.updatingPhotosCompGrid.current) this.getPhotosCompGrid();
  }

  componentWillUnmount() {
    this.timer && clearTimeout(this.timer);
    this.mounted.current = false;
  }

  addScrollBarView = (table) => {
    let scrollBar = document.querySelector(".scroll-bar");

    if (scrollBar) {
      this.adjustScrollBarHeight(table);
      scrollBar.addEventListener("scroll", () => {
        this.handleScroll(-1);
      });
      scrollBar.addEventListener("mouseover", () => this.hasMouseOver[-1] = true);
      scrollBar.addEventListener("mouseout", () => this.hasMouseOver[-1] = false);
    }
  };

  adjustSubjectHeight = (table) => {
    if (table.length > 1) {
      this.inputRefs[0].current.style.height = this.inputRefs[1].current.offsetHeight + "px";
    }
  }

  adjustScrollBarHeight = (table) => {
    let scrollBar = document.querySelector(".scroll-bar");
    let comp = table.length > 1 ? this.inputRefs[1] : this.inputRefs[0];
    if (scrollBar && comp.current.scrollHeight > comp.current.clientHeight) {
      scrollBar.children[0].style.height = comp.current.scrollHeight + "px";
      scrollBar.style.height = comp.current.offsetHeight + "px";
    }
  }

  handleScroll = (tableIndex) => {
    if (this.hasMouseOver[tableIndex]) {
      let scrollBar = document.querySelector(".scroll-bar");
      for (let i = 0; i < this.inputRefs.length; i++) {
        if (tableIndex === -1) {
          this.inputRefs[i].current.scrollTop = scrollBar.scrollTop;
        } else if (this.inputRefs[i] !== this.inputRefs[tableIndex]) {
          this.inputRefs[i].current.scrollTop = this.inputRefs[tableIndex].current.scrollTop;
        }
      }

      // set height of scroll bar if a comp was scrolled
      if (tableIndex !== -1) {
        scrollBar.scrollTop = this.inputRefs[tableIndex].current.scrollTop;
      }
      // Also fix the subject height on scroll
      this.adjustSubjectHeight(this.state.table);

    }
  };

  autoSave = debounce(() => this.postTableData(), 60 * 1000);

  // This method is to apply adjustment factor to comparables.
  calculateAdjustment = (tableIndex, event, type) => {
    const { adjustment } = this.state;
    const { gridData: { settings: { compGridFormCode } } } = this.props;
    const table = cloneDeep(this.state.table);
    if (type === "site") {
      calculateAdjustmentSite(type, table, tableIndex, adjustment, this.state.site);
    } else if (type === "age") {
      calculateAdjustmentAge(type, table, tableIndex, adjustment);
    } else if (type === "gla") {
      calculateAdjustmentGLA(type, table, tableIndex, adjustment);
    } else if (["above-grade"].includes(type)) {
      calculateAdjustmentAboveGrade(type, table, tableIndex, adjustment);
    }
    // calculate net adj
    calculateAdjTotal(table, compGridFormCode);
    this.setState({ table });
  };


  // Make POST API call to save updated table data.
  // noinspection ExceptionCaughtLocallyJS
  postTableData = () => {
    try {
      const { table } = this.state;
      const { settings } = this.props.gridData;
      if (hasRepeatedAddresses(table)) {
        throw new Error("Two or more comps cannot have the same address");
      }
      const { subject, comparables } = postCompData(table, settings.compGridFormCode);

      let compGrid = cloneDeep(this.props.gridData.compGrid);
      compGrid.subject = subject;
      // Transfer properties that should be persisted that might not be anywhere on the form
      compGrid.comparables.forEach((comp) => {
        let matchedComp = comparables.find(value => value.muellerPropertyId && comp.muellerPropertyId && value.muellerPropertyId === comp.muellerPropertyId);
        if (!matchedComp) matchedComp = comparables.find(value => value.oldId && comp.oldId && value.oldId === comp.oldId);
        if (matchedComp){
          matchedComp.imageURLs = matchedComp.imageURLs ?? comp.imageURLs;
          matchedComp.primaryPhotoId = matchedComp.primaryPhotoId ?? comp.primaryPhotoId;
        }
      });
      compGrid.comparables = comparables;
      this.props.storeGridData(this.props.controlNumber, { ...compGrid }, () => {
        validateComparables(this.props.controlNumber, (payload) => {
          this.setState({ serverValidationResult: payload });

          if (Array.isArray(payload) && payload.length) {
            let maxComp = payload.filter(e => e.key === "Max Comparable");
            if (maxComp.length > 0) this.props.makeToast(`Save successful - ${maxComp[0].message}`, "warning");
            else this.props.makeToast("Save successful with failed validation.", "warning");
          } else if (Array.isArray(payload) && !payload.length) {
            this.props.makeToast("Save successful. No validation errors.", "success");
          } else {
            throw new Error("Response message is of wrong type");
          }
        }, this.mounted);
        this.setState({ isPostingData: false, updated: false });
        this.props.formStatusChange(this.props.formTitle, "clean", this.props.formId);
        this.props.onFormSave();
      });
      this.setState({ isPostingData: true, errorKeys: validateTable(table, settings.compGridFormCode) });
      this.autoSave.cancel();
    } catch (error) {
      this.props.makeToast(error.message, "error");
    }
  };

  // Method to update data in state whenever new data is entered in table.
  updateTable = (e, photoData = null) => {
    let table = [...this.state.table];
    const { settings } = this.props.gridData;
    const type = photoData ? e.type : e.target.getAttribute("data-type");
    const index = photoData ? e.index : e.target.getAttribute("data-tableindex");
    const subTableKey = photoData ? e.subTableKey : e.target.getAttribute("data-subtablekey");
    const direction = photoData ? e.direction : e.target.getAttribute("data-direction");
    const value = (type === "photo" && photoData) ? photoData.Id : e.target.value;
    const oldValue = table[index][subTableKey][direction].value;
    const isAdjustment = (dir, ind) => dir === "right" && ind > 0;
    switch (type) {
      case "input": // This gets executed when the user changed value of an input box.
        // subject and comp desc
        if (!isAdjustment(direction, index)) {
          if (subTableKey === sections.SQUARE_FEET) {
            if (isValidValue(value, table[index][subTableKey][direction])) {
              table[index][subTableKey][direction].value = value;
              table[index][sections.ACRES][direction].value = Number(value / 43560).toFixed(2);
            } else {
              table[index][subTableKey][direction].value = oldValue;
            }
          }
          if (subTableKey === sections.ACRES) {
            if (isValidValue(value, table[index][subTableKey][direction])) {
              table[index][subTableKey][direction].value = value;
              table[index][sections.SQUARE_FEET][direction].value = Number(value * 43560).toFixed(0);
            } else {
              table[index][subTableKey][direction].value = oldValue;
            }
          } else if (["above-grade", "above-grade-bbt"].includes(get(table[0][subTableKey], ["row", "value"]))) {
            table[index][subTableKey][direction].value = isValidNumFieldValue(value, false, false) ? value : oldValue;
            let mappings = getAboveGradeUnitSubTableKeysFromGroupValue(get(table[0][subTableKey], ["row", "value"]));
            table[index][mappings.head][direction].options = aboveGradeUnitRoomCounts(table, index, direction, mappings);
          } else if ([sections.REC, sections.BED, sections.FULL_BATH, "Half Bath", sections.OTHER].includes(subTableKey)) {
            table[index][subTableKey][direction].value = isValidNumFieldValue(value, false, false) ? value : oldValue;
            table[index][sections.BELOW_GRADE_ROOM_COUNT][direction].options = belowGradeRoomCountOptions(table, index, direction);
          } else if ([sections.GLA, sections.GLA_BBT].includes(subTableKey)) {
            if (+value <= 99999) table[index][subTableKey][direction].value = Math.floor(Number(value)) || "";
            let gla = "";
            if (settings.compGridFormCode === CompGridFormCodes.BBT_COMPARABLES_GRID_MUELLER_ENTERPRISE) {
              gla = table[index][sections.GLA_BBT][direction].value;
            } else {
              gla = table[index][sections.GLA][direction].value;
            }
            let salePrice = table[index][sections.SALE_PRICE][direction].value;
            if (gla && salePrice) {
              table[index][sections.SALE_PRICE_GLA][direction].value = (salePrice / gla).toFixed(2);
            } else {
              table[index][sections.SALE_PRICE_GLA][direction].value = null;
            }
          } else if (subTableKey === sections.SALE_PRICE) {

            table[index][subTableKey][direction].value = isValidNumFieldValue(value, false, false) ? value : oldValue;
            let gla = "";
            if (settings.compGridFormCode === CompGridFormCodes.BBT_COMPARABLES_GRID_MUELLER_ENTERPRISE) {
              gla = table[index][sections.GLA_BBT][direction].value;
            } else {
              gla = table[index][sections.GLA][direction].value;
            }
            let salePrice = table[index][sections.SALE_PRICE][direction].value;
            if (gla && salePrice) {
              table[index][sections.SALE_PRICE_GLA][direction].value = (salePrice / gla).toFixed(2);
            } else {
              table[index][sections.SALE_PRICE_GLA][direction].value = null;
            }
          } else if ([sections.LATITUDE, sections.LONGITUDE].includes(subTableKey)) {
            table[index][subTableKey][direction].value = isValidNumFieldValue(value, true, true) ? value : oldValue;
            table = updateProximity(table);
          } else if (subTableKey === sections.DESIGN) {
            table[index][subTableKey][direction].value = value;
          } else {
            table[index][subTableKey][direction].value = isValidValue(value, table[index][subTableKey][direction]) ? value : oldValue;
          }
        } else if (isAdjustment(direction, index)) {
          // comps adj
          table[index][subTableKey][direction].value = isValidNumFieldValue(value, true, false) ? value : oldValue;
          updateAdjustment(table, index, subTableKey, direction);
          // calculate net adj
        } else {
          table[index][subTableKey][direction].value = value;
        }
        break;
      case "dropdown": // This gets executed when the user changed value of a Dropdown.
        if (subTableKey === sections.COMPARABLE_TYPE) {
          if (value === "list") {
            // clear both desc and adj value
            const clearSectionValue = (...fields) => fields.forEach(s => ["left", "right"].forEach(d => {
              if (table[index] && table[index][s] && table[index][s][d]) {
                if (s == sections.SALE_1004P) table[index][s]["left"].value = "Listing";
                else if (s == sections.STATUS_TYPE) {
                  table[index][s][d].value = "Active";
                }
                else table[index][s][d].value = "";
              }
            }));
            clearSectionValue(sections.SALE_PRICE, sections.SALE_PRICE_GLA, sections.DATE_OF_SALE_TIME, sections.STATUS_TYPE, sections.SALE_DATE_1004P,
              sections.CONTRACT_DATE, sections.SALE_OR, sections.FINANCING_CONCESSIONS, sections.SALE_1004P, sections.FINANCING_TYPE,
              sections.CONCESSION_AMOUNT, sections.CONCESSION_IF_OTHER);
          }
        }
        if ([sections.VIEW_RATING, sections.VIEW_TYPE, sections.VIEW_TYPE_2].includes(subTableKey)) {
          table[index][subTableKey][direction].value = value;
          if (table[index][sections.VIEW_TYPE][direction].value === "Other" || table[index][sections.VIEW_TYPE_2][direction].value === "Other") {
            table[index][sections.VIEW][direction].type = "input";
          } else {
            table[index][sections.VIEW][direction].type = "display";
          }
        } else if (subTableKey === "Is Age Estimated") {
          if (value === "Yes") table[index][subTableKey][direction].value = true;
          else if (value === "No") table[index][subTableKey][direction].value = false;
          else table[index][subTableKey][direction].value = null;
        } else if ([sections.LOCATION_TYPE, sections.LOCATION_TYPE_2].includes(subTableKey) && value !== "Other") {
          table[index][subTableKey][direction].value = value;
          const emptyLocationTypeOther = (locationTypeOtherSectionKey) => {
            if (table[index][locationTypeOtherSectionKey]) {
              table[index][locationTypeOtherSectionKey][direction].value = null;
            }
          };
          const getLocationTypeOther = {
            [sections.LOCATION_TYPE]: sections.LOCATION_TYPE_1_OTHER,
            [sections.LOCATION_TYPE_2]: sections.LOCATION_TYPE_2_OTHER
          };
          emptyLocationTypeOther(getLocationTypeOther[subTableKey]);
        }
        else {
          table[index][subTableKey][direction].value = value;
        }
        break;
      case "photo":
        if (subTableKey === sections.PRIMARY_PHOTO) {
          table[index][subTableKey][direction].value = value; 
        }
        break;
      default:
        table[index][subTableKey][direction].value = value;
        break;
    }

    //calculate UAD on all fields
    table = calculateUAD(table, this.props.gridData.settings);
    //calculate adjustment total
    calculateAdjTotal(table, settings.compGridFormCode);
    // validate the single field that was edited
    this.validateField(table, subTableKey, settings.compGridFormCode);
    this.setState({ table });
    // frequent calls to formStatusChange greatly impacted UI speed
    if (this.props.formStatus !== "dirty") {
      this.props.formStatusChange(this.props.formTitle, "dirty", this.props.formId);
    }
    this.autoSave();
  };

  // Toggle table rows.
  toggleDropdown = head => {
    let dropdownOpen = { ...this.state.dropdownOpen };
    dropdownOpen[head] = !this.state.dropdownOpen[head];
    this.setState({ dropdownOpen });
  };

  // Apply classnames to differentiate head rows and body rows.
  getTrclassName = row => {
    let name = row.type === "body" ? "body" : "head";
    return name;
  };

  // Apply classnames to apply text-align styles.
  getTdClassName = right => {
    let name = right ? "right" : "left";
    return name;
  };

  getApplyBodyClassName = type => {
    if (["condition", "basement-finished", "above-grade", "quality"].includes(type)) {
      return "applyBodyLarge";
    }
    return "applyBody";
  };

  copySubjectToComp = subTableKey => {
    let table = cloneDeep(this.state.table);
    let [subject] = table.splice(0, 1);
    let newTable = table.map((comp, index) => {
      for (let section in comp) {
        if (comp[section]
          && comp[section].row
          && comp[section].row.value === subject[subTableKey].row.value
          && !comp[section].row.adjustment
          && !comp[section].left.value
          && (subject[section].right.value || subject[section].right.value === 0)
        ) {
          comp[section].left.value = subject[section].right.value;

          if (["above-grade"].includes(get(table[0][section], ["row", "value"]))) {
            let mappings = getAboveGradeUnitSubTableKeysFromGroupValue(get(table[0][section], ["row", "value"]));
            comp[mappings.head].left.options = aboveGradeUnitRoomCounts(table, index, "left", mappings);
          }
          if ([sections.REC, sections.BED, sections.FULL_BATH, sections.HALF_BATH, sections.OTHER].includes(section)) {
            comp[sections.BELOW_GRADE_ROOM_COUNT].left.options = belowGradeRoomCountOptions(table, index, "left");
          }
        }
      }
      return comp;
    });

    // update UAD Code
    newTable = calculateUAD(newTable, this.props.gridData.settings);
    newTable.splice(0, 0, subject);
    this.validateField(newTable, subTableKey, this.props.gridData.settings.compGridFormCode);
    this.setState({ table: newTable });
  };

  getDateValue = (value) => {
    if (value) {
      let endIndex = value.indexOf("T");
      return endIndex < 0 ? value : value.substring(0, endIndex);
    }
    return "";
  }

  handleCellFocus = (e) => {
    let subTableKey = e.target.getAttribute("data-subtablekey");
    // validate the single field that was edited
    this.validateField(this.state.table, subTableKey, this.props.gridData.settings.compGridFormCode);
  }
  // no invalid value except 0
  noNullInput = (value) => {
    if (!value) {
      if (value !== 0) return "";
      else value;
    }
    return value;
  }
  toggleGarageDisplayFields = (table, stayHidden = false) => {
    let t = cloneDeep(table || this.state.table);
    let garageSections = getGarageSectionsByFormCode(table[0]);
    let hideCarStorageExtra = stayHidden || !this.state.hideCarStorageExtra;
    if (hideCarStorageExtra) {
      garageSections.map(subTableKey => {
        range(t.length).map(tabIndex => {
          if (tabIndex === 0) {
            t[tabIndex][subTableKey]["right"].value = "";
          } else {
            ["left", "right"].map(dir => {
              t[tabIndex][subTableKey][dir].value = "";
            });
          }
        });
      });
      t = calculateUAD(t, this.props.gridData.settings);
      this.setState({ table: t });
    }
    this.setState({ hideCarStorageExtra: hideCarStorageExtra });
    return t;
  }

  updateAddress = (address = {}, index, dir) => {
    const hasValue = val => !!val || val === 0 || val === "";
    let table = cloneDeep(this.state.table);
    if (hasValue(address.streetAddress)) table[index][sections.ADDRESS][dir].value = address.streetAddress;
    if (hasValue(address.city)) table[index][sections.CITY][dir].value = address.city;
    if (hasValue(address.state)) table[index][sections.STATE][dir].value = address.state;
    if (hasValue(address.zip)) table[index][sections.ZIP][dir].value = address.zip;
    if (hasValue(address.latitude)) table[index][sections.LATITUDE][dir].value = address.latitude;
    if (hasValue(address.longitude)) table[index][sections.LONGITUDE][dir].value = address.longitude;
    table = updateProximity(table);
    this.setState({ table });
    this.postTableData();
  }

  getPhotosCompGrid = () => {
    const { controlNumber, token, makeToast, saveCompPhotoMetadata } = this.props;
    fetch(`${WebConfig.photoServiceRoot}/${this.customer.current?.number}/${Number(controlNumber)}?SSO=${token}`, {
      method: "GET",
    }).then(res => res.json()).then(res2 => {
      this.updatingPhotosCompGrid.current = false; 
      saveCompPhotoMetadata(res2);
    }).catch(() => makeToast("There was an error getting all comparable photos.", "error"));
  }

  addPhotoCompGrid = (e) => {
    let ecopy = {
      type: e.target.getAttribute("data-type"),
      index: e.target.getAttribute("data-tableindex"),
      subTableKey:  e.target.getAttribute("data-subtablekey"),
      direction: e.target.getAttribute("data-direction"),
    };
    let table = cloneDeep(this.state.table);
    let numIndex = Number(ecopy.index);
    if (!table[numIndex]["Address"][ecopy.direction].value) return this.props.makeToast("Address required before uploading photo.", "error");
    const file = e.target.files[0];
    const fileData = new Blob([file]);
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      const data = reader.result;
      const { controlNumber, token, makeToast, gridData } = this.props;
      // cannot complete browser upload from an XMLHttpRequest, so using fetch method instead
      fetch(`${WebConfig.photoServiceRoot}/${this.customer.current?.number}/${Number(controlNumber)}?SSO=${token}`, {
        method: "POST",
        body: data
      }).then(res => res.json()).then(res => {
        this.updatingPhotosCompGrid.current = true; 
        if (res) {
          let photoTag = gridData.compGrid.comparables[numIndex - 1].comparableType === "sold" ? `Comparable ${numIndex}` : `Listing ${numIndex}`;
          res.Tags.push(photoTag);
          fetch(`${WebConfig.photoServiceRoot}/${this.customer.current?.number}/${Number(controlNumber)}/${res.Id}?SSO=${token}`, { // need photoId in order to add photoTags and update description
            method: "PUT",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify(res)
          });
        }
        return this.updateTable(ecopy, res);
      }).catch(() => makeToast("There was an error saving your photo.", "error")); 
    }, false);
    reader.readAsArrayBuffer(fileData);
  }

  removePhotoCompGrid = (tableIdx, photoId) => {
    const { compPhotoMetadata, controlNumber, token, makeToast } = this.props;
    let found = compPhotoMetadata.find(cpm => cpm.Id === photoId); 
    let img = document.getElementById(`primary-photo-Primary photo-${tableIdx}`);
    let targetElem = {
      type: img.getAttribute("data-type"),
      index: img.getAttribute("data-tableindex"),
      subTableKey: img.getAttribute("data-subtablekey"),
      direction: img.getAttribute("data-direction"),
      target: { value: null }
    };
    if (found) {
      fetch(`${WebConfig.photoServiceRootDelete}/${this.customer.current?.number}/${Number(controlNumber)}/${found.Id}?SSO=${token}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(found)
      }).then(res => res.json()).then(() => {
      this.updatingPhotosCompGrid.current = true;
      return this.updateTable(targetElem, {Id: null});
      });
    } else return makeToast("There was an error removing your photo.", "error");
  }

  // Method to render Tables. One table for subject and one for each comparables.
  // TODO: this is probably supposed to be a component
  // eslint-disable-next-line react/display-name
  renderTable = (tableItem, tableIndex) => {
    const { dropdownOpen, table } = this.state;
    const defineTableCells = (tableItem, tableIndex, subTableKey, dir) => {
      let cellType = tableItem[subTableKey][dir].type;
      if (typeof tableItem[subTableKey][dir].type === "function") {
        cellType = tableItem[subTableKey][dir].type(table, tableIndex, subTableKey, dir);
      }
      return (
        <React.Fragment>
          {cellType === "display" && (
            <div className={`tableDisplay${tableIndex === 0 && dir === "left" && this.state.errorKeys.includes(subTableKey) ? " comp-grid-error" : ""}`} onChange={() => { }}>
              <div className="tableDisp" style={get(tableItem[subTableKey], ["row", "tabs"]) ? { paddingLeft: 21 * tableItem[subTableKey].row.tabs } : {}}>{(subTableKey === sections.PROXIMITY_TO_SUBJECT && tableIndex > 0) ? this.noNullInput(get(tableItem[subTableKey][dir], ["value", "displayValue"])) : this.noNullInput(tableItem[subTableKey][dir].value)}
                {tableIndex === 0 && (subTableKey === sections.CAR_STORAGE_1 || subTableKey === sections.CAR_STORAGE_1_BBT) && !this.props.readOnly && (
                  <Icon className="icon" onClick={() => this.toggleGarageDisplayFields()} style={this.state.disableAddCarStorageExtra ? { display: "none" } : {}}>
                    {this.state.hideCarStorageExtra ? "add" : "remove"}
                  </Icon>
                )}
              </div>
            </div>
          )}
          {cellType === "input" && (
            <input
              className="tableInput"
              value={this.noNullInput(tableItem[subTableKey][dir].value)}
              data-tableindex={tableIndex}
              data-subtablekey={subTableKey}
              data-direction={dir}
              data-type={cellType}
              onChange={this.updateTable}
              onFocus={this.handleCellFocus}
            />
          )}
          {cellType === "none" && <div className="tableNone" onChange={() => { }} />}
          {cellType === "dropdown" && (
            <select
              data-tableindex={tableIndex}
              data-subtablekey={subTableKey}
              data-direction={dir}
              data-type={cellType}
              onChange={this.updateTable}
              className="drop-select"
              name={tableItem[subTableKey][dir].value}
              value={this.noNullInput(tableItem[subTableKey][dir].value)}
              onFocus={this.handleCellFocus}
            >
              {tableItem[subTableKey][dir].options.map((opt, i) => {
                return (
                  <option key={`drop-option-${dir}-${i}`} className="drop-option" value={opt.value}>
                    {opt.label}
                  </option>
                );
              })}
            </select>
          )}
          {cellType === "room-count" && (
            <div className="tableDisplay" onChange={() => { }}>
              {tableItem[subTableKey][dir].options.map((triItm, i) => {
                return (
                  <div key={`room-count-${dir}-${i}`} className={`${tableItem[subTableKey][dir].options.length === 3 ? "display-three" : "display-four"}`}>
                    {triItm}
                  </div>
                );
              })}
            </div>
          )}
          {cellType === "date" && (
            <TextField
              id="date"
              type="date"
              className="compgrid-datepicker"
              onChange={this.updateTable}
              value={this.getDateValue(tableItem[subTableKey][dir].value)}
              InputLabelProps={{
                shrink: true
              }}
              inputProps={{
                "data-tableindex": tableIndex,
                "data-subtablekey": subTableKey,
                "data-direction": dir,
                "data-type": cellType
              }}
              disabled={this.props.readOnly}
            />
          )}
          {cellType === "google-autocomplete" && (
            <SearchGooglePlaces
              id={`search-google-places-${tableIndex}`}
              onChange={(address) => this.updateAddress(address, tableIndex, dir)}
              value={tableItem[subTableKey][dir].value}
              streetAddressOnly
            />
          )}
          {cellType === "textarea" && (
            <textarea
              id={`textarea-${subTableKey}-${tableIndex}`}
              className="compgrid-textarea"
              rows="4"
              value={this.noNullInput(tableItem[subTableKey][dir].value)}
              data-tableindex={tableIndex}
              data-subtablekey={subTableKey}
              data-direction={dir}
              data-type={cellType}
              onChange={this.updateTable}
              onFocus={this.handleCellFocus}
            />
          )}
          {cellType === "photo" && 
          (<div className={`primary-photo-${subTableKey}-${tableIndex}`}> 
            {(tableItem[subTableKey][dir].value !== null && this.props.compPhotoMetadata.find(cpm => cpm.Id === tableItem[subTableKey][dir].value)) ? <>
              <img 
                id={`primary-photo-${subTableKey}-${tableIndex}`}
                alt={`primary-photo-${subTableKey}-${tableIndex}`} 
                src={`${WebConfig.photoServiceRoot}/${this.customer.current?.number}/${Number(this.props.controlNumber)}/${tableItem[subTableKey][dir].value}?SSO=${this.props.token}`}
                height="200" 
                width="300" 
                data-tableindex={tableIndex}
                data-subtablekey={subTableKey}
                data-direction={dir}
                data-type={cellType}
              />
              <Icon className="icon" onClick={() => this.removePhotoCompGrid(tableIndex, tableItem[subTableKey][dir].value)}> 
                  delete
              </Icon>
            </> : 
              <form className={"primary-photo-form"}>
                {tableIndex !== 0 && <>
                  <input 
                    type={"file"} 
                    id={`primary-photo-${subTableKey}-${tableIndex}-input`} 
                    accept={"image/*"} 
                    style={{ display: "none" }}
                    onChange={this.addPhotoCompGrid}
                    data-tableindex={tableIndex}
                    data-subtablekey={subTableKey}
                    data-direction={dir}
                    data-type={cellType}
                  />
                  <label
                    style={{ height: "100%" }}
                    className={`primary-photo-${subTableKey}-${tableIndex}-button`} 
                    htmlFor={`primary-photo-${subTableKey}-${tableIndex}-input`}>
                    Click here to <br/> upload an image
                  </label> 
                </>}
              </form>}
            </div>
          )}
        </React.Fragment>
      );
    };
    const getPlaceHolder = (subTableKey, title) => {
      if ((tableItem[subTableKey].row.value === "gla" && title === "Adj Factor")
        || tableItem[subTableKey].row.value === "age"
        || (tableItem[subTableKey].row.value === "site" && title === "Adj Factor"))
        return "$";
      return "";
    };

    const stickyClassNames = {
      [sections.ADDRESS]: "sticky-address",
      [sections.CITY]: "sticky-city",
      [sections.STATE]: "sticky-state"
    };

    const dropdownRepeatButtons = (tableIndex, tableItem, subTableKey, dropdownOpen, copySubjectBlacklist) => (
      <>
        {tableIndex === 0 && tableItem[subTableKey].row.type === "head" && tableItem[subTableKey].row.showIcon && !dropdownOpen[tableItem[subTableKey].row.value] && (
          <Icon className="icon" onClick={() => {
            this.toggleDropdown(tableItem[subTableKey].row.value);
          }}>
            keyboard_arrow_down
          </Icon>
        )}
        {tableIndex === 0 && tableItem[subTableKey].row.type === "head" && tableItem[subTableKey].row.showIcon && dropdownOpen[tableItem[subTableKey].row.value] && (
          <Icon className="icon" onClick={() => {
            this.toggleDropdown(tableItem[subTableKey].row.value);
          }}>
            keyboard_arrow_up
          </Icon>
        )}
        {tableIndex === 0 && tableItem[subTableKey].row.type === "head" &&
          (!copySubjectBlacklist.includes(subTableKey)) && !this.props.readOnly && (
            <Icon className="icon" onClick={() => this.copySubjectToComp(subTableKey)}>
              repeat
            </Icon>
          )}
      </>
    );
    const comparableCount = comparableCountByFormCode[this.props.gridData.settings.compGridFormCode];
    const disableAddComparable = this.state.table.length - 1 >= comparableCount.maxComparables;
    return (
      <table ref={this.inputRefs[tableIndex]}>
        <tbody>
          {Object.keys(tableItem).map((subTableKey, rowIndex) => {
            if (subTableKey === "Feature") {
              return (
                <tr key={`${subTableKey}-${rowIndex}`}>
                  <th className="sticky th-feature">
                    {tableIndex === 0
                      ? tableItem[subTableKey]["left"].value
                      : <>
                        {`${tableItem[subTableKey]["left"].value} # ${tableIndex}`}
                        {Array.isArray(tableItem[subTableKey]["comp"].imageURLs) && tableItem[subTableKey]["comp"].imageURLs.length > 0 &&
                          <Icon className="icon" onClick={() => this.props.onPhotoView(tableItem[subTableKey]["comp"].imageURLs)}>
                            image
                        </Icon>}
                      </>
                    }
                  </th>
                  <th className="sticky th-subject">
                    {tableIndex === 0 && rowIndex === 0 && !disableAddComparable && !this.props.readOnly && (
                      <Icon className="icon" onClick={() => {
                        this.setState((state, props) => ({ table: state.table.concat([formSpecificComparableSections(null, props.gridData, true, props.states, props.readOnly)]) }));
                        this.inputRefs = this.inputRefs.concat(React.createRef());
                      }}>
                        add
                      </Icon>
                    )}
                    {tableIndex > 0 && rowIndex === 0 && !this.props.readOnly && (
                      <Icon className="icon" onClick={() => {
                        this.setState((state) => ({ table: state.table.filter((x, i) => i !== tableIndex) }));
                        this.inputRefs = this.inputRefs.filter((x, i) => i !== tableIndex);
                      }}>
                        delete
                      </Icon>
                    )}
                    {tableItem[subTableKey]["right"].value}
                  </th>
                </tr>
              );
            }
            if (
              !tableItem[subTableKey].row.adjustment &&
              (tableItem[subTableKey].row.type === "head" || (tableItem[subTableKey].row.type === "body" && dropdownOpen[tableItem[subTableKey].row.value])) &&
              [sections.ADDRESS, sections.CITY, sections.STATE].includes(subTableKey)
            ) {
              return (
                <tr key={`${subTableKey}-${rowIndex}`} className={this.getTrclassName(tableItem[subTableKey].row)}>
                  <th className={`${this.getTdClassName(tableItem[subTableKey].left.right)} ${stickyClassNames[subTableKey]}`}>
                    {dropdownRepeatButtons(tableIndex, tableItem, subTableKey, dropdownOpen, copySubjectBlacklist)}
                    {defineTableCells(tableItem, tableIndex, subTableKey, "left")}
                  </th>
                  <th className={`${this.getTdClassName(tableItem[subTableKey].right.right)}${tableItem["Feature"]["left"].value !== "Feature" ? " td-adjustment" : ""} ${stickyClassNames[subTableKey]}`}>{defineTableCells(tableItem, tableIndex, subTableKey, "right")}</th>
                </tr>
              );
            }
            else if (
              !tableItem[subTableKey].row.adjustment &&
              (tableItem[subTableKey].row.type === "head" || (tableItem[subTableKey].row.type === "body" && dropdownOpen[tableItem[subTableKey].row.value])) &&
              !(getGarageSectionsByFormCode(tableItem).includes(subTableKey) && this.state.hideCarStorageExtra)
            ) {
              return (
                <tr key={`${subTableKey}-${rowIndex}`} className={this.getTrclassName(tableItem[subTableKey].row)}>
                  <td className={`${this.getTdClassName(tableItem[subTableKey].left.right)}${tableItem["Feature"]["left"].value === "Feature" ? " td-feature" : ""}`}>
                    {dropdownRepeatButtons(tableIndex, tableItem, subTableKey, dropdownOpen, copySubjectBlacklist)}
                    {defineTableCells(tableItem, tableIndex, subTableKey, "left")}
                  </td>
                  <td className={`${this.getTdClassName(tableItem[subTableKey].right.right)}${tableItem["Feature"]["left"].value !== "Feature" ? " td-adjustment" : ""}`}>{defineTableCells(tableItem, tableIndex, subTableKey, "right")}</td>
                </tr>
              );
            } else if (tableItem[subTableKey].row.adjustment) {
              if (dropdownOpen[tableItem[subTableKey].row.value]) {
                return (
                  <React.Fragment>
                    <tr className="adjustment-list-item">
                      <td colSpan="2" style={{ borderRight: "1px solid #ccc" }}>
                        <div className="applyLink" onClick={event => this.calculateAdjustment(tableIndex, event, tableItem[subTableKey].row.value)}>
                          {tableIndex === 0 ? "Apply to all" : "Apply"}
                        </div>
                      </td>
                    </tr>
                    {Object.keys(adjustments).includes(tableItem[subTableKey].row.value) && (
                      <React.Fragment>
                        {tableIndex === 0 && adjustments[tableItem[subTableKey].row.value].map((title, index) => (
                          <tr key={`${title}-${index}`} className="adjustment-list-item">
                            <td>
                              <p className="adjustment-title">{title}</p>
                            </td>
                            <td className="adjustment-input">
                              <tr>
                                <td>
                                  <input className="adjustment-apply"
                                    onChange={(e) => (isValidNumFieldValue(e.target.value, false, false)) && this.setState({
                                      adjustment: {
                                        ...this.state.adjustment,
                                        [tableItem[subTableKey].row.value]: {
                                          ...this.state.adjustment[tableItem[subTableKey].row.value],
                                          [title]: e.target.value
                                        }
                                      }
                                    })}
                                    value={(this.state.adjustment && this.state.adjustment[tableItem[subTableKey].row.value] && this.state.adjustment[tableItem[subTableKey].row.value][title]) || ""}
                                    placeholder={getPlaceHolder(subTableKey, title)}
                                  />
                                </td>
                                <td className="adjustment-unit">
                                  {tableItem[subTableKey].row.value === "site" &&
                                    <span>
                                      {title === "Adj Factor" ? "per sf" : "sf"}
                                      <input type="radio" id={`radio-${title}-1`} value="sf"
                                        checked={this.state.site[title] === "sf"} onChange={(e) => this.setState({ site: { ...this.state.site, [title]: e.target.value } })} />
                                      /ac
                                       <input type="radio" id={`radio-${title}-2`} value="ac"
                                        checked={this.state.site[title] === "ac"} onChange={(e) => this.setState({ site: { ...this.state.site, [title]: e.target.value } })} />
                                    </span>
                                  }
                                  {tableItem[subTableKey].row.value === "age" &&
                                    <span>/yr</span>
                                  }
                                  {tableItem[subTableKey].row.value === "gla" && title === "Adj Threshold" &&
                                    <span>sf</span>
                                  }
                                  {tableItem[subTableKey].row.value === "gla" && title === "Adj Factor" &&
                                    <span>/sf</span>
                                  }
                                </td>

                              </tr>
                            </td>
                          </tr>
                        ))}
                        {tableIndex > 0 && adjustments[tableItem[subTableKey].row.value].map((title, index) => (<tr className="adjustment-list-item" key={`${title}-${index}`}><td /><td className="adjustment-input" /></tr>))}
                        <tr style={{ borderTop: "none" }} className="adjustment-list-item"><td colSpan="2" /></tr>
                      </React.Fragment>
                    )}
                  </React.Fragment>
                );
              }
              return null;
            }
            return null;

          })}
        </tbody>
      </table>
    );
  };

  onDragEnd = result => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    let table = reorder(this.state.table, result.source.index, result.destination.index);
    this.setState({ table });
  };

  validateField = (table, subTableKey, compGridFormCode) => {
    // for all tables
    let newErrorKeys = validateField(table, subTableKey, this.state.errorKeys, compGridFormCode);
    this.setState({ errorKeys: newErrorKeys });
  };

  compGridsAllowingReorder = [
    CompGridFormCodes.URAR_1004_APPRAISALCOMPARABLES, 
    CompGridFormCodes.CHASE_SERVICING_COMPARABLES,
    CompGridFormCodes.COMPARABLES_GRID_MUELLER_ENTERPRISE,
    CompGridFormCodes.COLLATERAL_APPRAISAL_COMPARABLES,
  ];

  render() {
    const { table, isFetchingGrid, isPostingData } = this.state;
    const lastSavedUtcDateTime = get(this.props, ["gridData", "compGrid", "lastSavedUtcDateTime"]);
    if (isFetchingGrid) return <MSpinner />;

    const SaveButtonPortal = () =>
      ReactDOM.createPortal(
        <div>
          <Button
            id="new-comp-grid-save"
            variant="text"
           
            onClick={() => this.postTableData()}
            style={{ marginRight: "15px" }}
            disabled={isPostingData ? true : false}
          >
            {isPostingData ? "Saving..." : "Save"}
          </Button>
          {lastSavedUtcDateTime && <span className="comp-grid-last-save">{`Last save: ${dateTimeToString(new Date(lastSavedUtcDateTime))}`}</span>}
        </div>
        , document.querySelector(".comp-search-header")
      );
    return (
      <React.Fragment>
        {!this.props.isDemo && !this.props.readOnly &&
          <SaveButtonPortal />
        }
        <LoadingOverlay active={isPostingData} fadeSpeed={250} spinner={<MSpinner />} text="Saving" className="comp-grid-overlay">
          <div className="comparableGridWrapper">
            <div style={{ display: "flex", height: "100%" }}>
              <DragDropContext onDragEnd={this.onDragEnd}>
                <div className="subject-table">{this.renderTable(table[0], 0)}</div>
                <Droppable droppableId="droppable" direction="horizontal">
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} className={`droppable${snapshot.isDraggingOver ? ` droppable-dragging` : ``}`} {...provided.droppableProps}>
                      {table.map((item, index) => {
                        if (index === 0) {
                          return null;
                        }
                        return (
                          <Draggable
                            key={"comparable-" + index}
                            draggableId={"drag-" + index}
                            index={index}
                            isDragDisabled={(
                                this.compGridsAllowingReorder.includes(this.props.gridData?.settings?.compGridFormCode) ? false : true
                            ) ||
                              this.props.readOnly
                            }>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={snapshot.isDragging ? "draggable-dragging" : ""}
                                style={provided.draggableProps.style}
                              >
                                <div key={`table-${index}`} style={{ height: "100%" }}>{this.renderTable(item, index)}</div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <div className="scroll-bar"><div /></div>
            </div>
          </div>
        </LoadingOverlay>
      </React.Fragment>
    );
  }
}


ComparableGrid.propTypes = {
  getGridData: PropTypes.func.isRequired,
  postingGridData: PropTypes.bool.isRequired,
  gridData: PropTypes.object.isRequired,
  controlNumber: PropTypes.string.isRequired,
  storeGridData: PropTypes.func.isRequired,
  compPhotoMetadata: PropTypes.array,
  saveCompPhotoMetadata: PropTypes.func,
  //No need of this to be in prod
  isDemo: PropTypes.bool,
  
  states: PropTypes.array,
  getStates: PropTypes.func.isRequired,
  formStatusChange: PropTypes.func,
  validateComparables: PropTypes.func,
  formTitle: PropTypes.string,
  formId: PropTypes.string,
  formStatus: PropTypes.string,
  readOnly: PropTypes.bool,
  onPhotoView: PropTypes.func.isRequired,
  onFormSave: PropTypes.func.isRequired,
  token: PropTypes.string,
};

const mapStateToProps = state => {
  return {
    gridData: state.comparable.gridData,
    controlNumber: state.caseDetails.controlNumber,
    postingGridData: state.comparable.postingGridData,
    compPhotoMetadata: state.caseForms.compPhotoMetadata,

    states: state.region.states,
    pnxValidation: state.caseForms.pnxValidation,
    makeToast: PropTypes.func.isRequired,
    token: state.user.profile.token,
  };
};

const mapDispatchToProps = dispatch => ({
  getGridData(controlNumber) {
    dispatch(getGridData(controlNumber));
  },
  storeGridData(controlNumber, data, callback) {
    dispatch(storeGridData(controlNumber, data, callback));
  },
  getStates() {
    dispatch(getStates());
  },
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
  savePNXFormSuccess() {
    dispatch(savePNXFormSuccess());
  },
  savePNXFormFail(text) {
    dispatch(savePNXFormFail(text));
  },
  saveCompPhotoMetadata(payload) {
    dispatch(saveCompPhotoMetadata(payload));
  }
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(ComparableGrid);
